import {Box, Button, IconButton, InputAdornment, styled, TextField} from "@mui/material";
import React, {useEffect, useState} from "react";
import ThemedText from "../../text";
import {LOGO_2} from "../../../constants/images";
import Label from "../../../resources/label";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {useAuth} from "../../../providers/AuthProvider";
import theme, {FONTS} from "../../../constants/theme";
import {scale} from "../../../utils";

interface Props {
  style?: React.CSSProperties;
  onChangeSuccess: () => void;
}

const LogoImage = styled('img')({
  height: scale(113),
  width: scale(280.42),
  marginBottom: scale(50),
});

const InputWrapper = styled('div')({
  width: scale(400),
  height: scale(35),
  display: 'flex',
  rowGap: 2,
  flexDirection: 'column',
  flexBasis: '50%',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  boxSizing: 'border-box',
});

const ThemedButton = styled(Button)({
  borderRadius: scale(30),
  width: scale(150),
  height: scale(52),
  marginTop: scale(40),
  marginBottom: scale(40),
});

const ForgotPasswordForm = ({style, onChangeSuccess}: Props) => {
  const auth = useAuth();
  const [userError, setUserError] = useState(false);
  const [passError, setPassError] = useState(false);
  const [username, setUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userErrorLabel, setUserErrorLabel] = useState('');
  const [passErrorLabel, setPassErrorLabel] = useState('');

  useEffect(() => {
    const usernameInput = document.querySelector('input[placeholder="Username"]') as HTMLInputElement;
    const passwordInput = document.querySelector('input[placeholder="Password"]') as HTMLInputElement;

    if (usernameInput?.value) setUsername(usernameInput.value);
    if (passwordInput?.value) setNewPassword(passwordInput.value);
  }, []);



  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isButtonDisabled()) {
      setUserError(true);
      setPassError(true);
      return;
    }
    auth && auth.changePasswordAction({username, newPassword}).then((response) => {
      if (response === 'Invalid Account Type') {
        setUserError(true);
        setPassError(true);
        setUserErrorLabel('Tipo utente non valido');
        setPassErrorLabel('Tipo utente non valido');
        return;
      }
      if (response === 'Server Error') {
        setUserError(true);
        setPassError(true);
        setUserErrorLabel('Errore nel server contattare l\'amministratore');
        setPassErrorLabel('Errore nel server contattare l\'amministratore');
        return;
      }
      if (response !== 'Ok') {
        setUserError(true);
        setPassError(true);
        setUserErrorLabel('Verificare i dati inseriti');
        setPassErrorLabel('Verificare i dati inseriti');
        return;
      }
      onChangeSuccess()
    });
  };

  const slotProps = {
    input: {
      sx: {
        fontFamily: FONTS.MONTSERRAT,
        fontWeight: 500,
        fontSize: scale(24),
        color: theme.palette.ceramic.contrastText,
        height: scale(40),
        boxSizing: 'border-box',
        backgroundColor: 'transparent',
        borderRadius: 0,
        '& > input': {
          height: scale(40),
          paddingTop: scale(7),
          paddingLeft: scale(8),
          paddingBottom: scale(6),
          boxSizing: 'border-box',
        },
      },
    },
  }
  const slotPasswordProps = {
    input: {
      sx: {
        fontFamily: FONTS.MONTSERRAT,
        fontWeight: 500,
        fontSize: scale(24),
        color: theme.palette.ceramic.contrastText,
        height: scale(40),
        boxSizing: 'border-box',
        borderRadius: 0,
        '& > input': {
          height: scale(40),
          paddingTop: scale(7),
          paddingLeft: scale(8),
          paddingBottom: scale(6),
          boxSizing: 'border-box',
        }
      },
      endAdornment:
      <InputAdornment
        position="end"
        style={{
          position: 'relative',
          right: scale(18),
          height: scale(25),
          width: scale(25),
        }}
      >
        <IconButton
          aria-label={
            showPassword ? 'hide the password' : 'display the password'
          }
          onClick={() => setShowPassword((show) => !show)}
          onMouseDown={(event) => event.preventDefault()}
          onMouseUp={(event) => event.preventDefault()}
          edge="end"
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>

    }
  }

  const inputRootStyle = {
    borderRadius: 0,
    '& .MuiInput-underline:before': {
      borderBottom: '1px solid',
      borderColor: theme.palette.primary.main,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: '1px solid',
      borderColor: theme.palette.primary.main,
    },
  }

  const isButtonDisabled = (): boolean => {
    return (!username.trim() || !newPassword.trim())
  }

  return (
    <Box
      component="form"
      noValidate={true}
      autoComplete="true"
      onSubmit={(e) => handleLogin(e)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        ...style
      }}
    >
      <LogoImage alt={'logo_header'} src={LOGO_2}/>
      <ThemedText
        fontSize={scale(24)}
        align={'center'}
        fontWeight={700}
        lineHeight={scale(66)}
        letterSpacing={scale(0)}
        text={Label.FORGOT_LABEL}
        color={'primary'}
      />
      <InputWrapper>
        <TextField
          placeholder="Username"
          variant="standard"
          color={'primary'}
          value={username}
          size={'small'}
          slotProps={slotProps}
          onChange={(event) => {
            setUserError(false)
            setUsername(event.target.value)
          }}
          onBlur={(event) => {
            setUserError(false)
            setUsername(event.target.value);
          }}
          error={userError}
          sx={inputRootStyle}
          helperText={userErrorLabel}
        />
      </InputWrapper>
      <InputWrapper>
        <TextField
          placeholder="Password"
          variant="standard"
          color={'primary'}
          value={newPassword}
          size={'small'}
          type={showPassword ? 'text' : 'password'}
          slotProps={slotPasswordProps}
          onChange={(event) => {
            setPassError(false)
            setNewPassword(event.target.value)
          }}
          onBlur={(event) => {
            setPassError(false)
            setNewPassword(event.target.value);
          }}
          error={passError}
          sx={inputRootStyle}
          helperText={passErrorLabel}
        />
      </InputWrapper>
      <ThemedButton type={'submit'} variant="contained" disabled={userError || passError}>
        <ThemedText text={'conferma'} fontWeight={600} fontSize={scale(18)}/>
      </ThemedButton>
    </Box>
  )
}

export default ForgotPasswordForm
